import * as React from 'react';
import {Link as RouterLink} from 'react-router-dom';

import {SegmentedControl} from 'shells/card/segmented-control';
import {Stack} from 'shells/layout/stack/stack';
import {Link} from 'shells/link/link';
import {LoadingText} from 'shells/loading-placeholder/loading-text';
import type {SavedSettingToastState} from 'shells/saved-setting-toast/saved-setting-toast';
import {Switch} from 'shells/switch/switch';
import {CharacterLimitedTextfield} from 'shells/textfield/character-limited-textfield';
import {Body} from 'shells/typography/body';
import {LabelWithContent} from 'shells/typography/label-with-content';

import type {NudgeDeviceVisibilityEnum} from 'nutshell-graphql-types';
import type {ChatSettingsLookAndFeelInput} from 'nutshell-graphql-types';

import {useDebouncedSaveField} from '../actions-options/use-debounced-save-field';
import {SAVE_DELAY, SAVE_STATE_KEYS, NUDGE_DEVICE_VISIBILITY_OPTIONS} from '../consts';
import type {SaveStateKey} from '../consts';

type Props = {
    isLoading: boolean;
    toastState: SavedSettingToastState;
    handleSave: (updates: ChatSettingsLookAndFeelInput, saveStateKey: SaveStateKey) => Promise<any>;
    isNudgeEnabled: boolean;
    setIsNudgeEnabled: (isNudgeEnabled: boolean) => void;
    nudgeCallToAction: string;
    nudgeDeviceVisibility: NudgeDeviceVisibilityEnum;
    setNudgeDeviceVisibility: (deviceVisibility: NudgeDeviceVisibilityEnum) => void;
    savingToastKey: SaveStateKey | null | undefined;
    hasNoKnowledgeContent: boolean;
};

export const NUDGE_MESSAGE_LIMIT = 50;
export const NUDGE_CALL_TO_ACTION_LIMIT = 25;

export function MinimizedViewSection(props: Props) {
    const {
        value: nudgeCallToAction,
        onChange: setNudgeCallToAction,
        onBlur: onNudgeCallToActionBlur,
    } = useDebouncedSaveField({
        savedValue: props.nudgeCallToAction,
        handleSave: (newNudgeCallToAction) =>
            props.handleSave(
                {nudgeSettings: {callToAction: newNudgeCallToAction}},
                SAVE_STATE_KEYS.CALL_TO_ACTION
            ),
        validation: {maxLength: NUDGE_CALL_TO_ACTION_LIMIT},
    });

    // If nudges are enabled but there is no knowledge content, we show them as disabled
    const areNudgesShownAsDisabled = props.isNudgeEnabled && !props.hasNoKnowledgeContent;

    return (
        <div className='pb-16'>
            <Stack spacing={16}>
                <Body size='large' isBold={true}>
                    Minimized view
                </Body>
                <Body>
                    Before users initiate a conversation or select an option, they will see a
                    concise version of the Engagement bar.
                </Body>
                <LabelWithContent
                    label='Smart nudges'
                    state={
                        props.savingToastKey === SAVE_STATE_KEYS.SMART_NUDGES
                            ? props.toastState
                            : 'initial'
                    }
                    spacing={8}
                    content={
                        props.isLoading ? (
                            <LoadingText isFullWidth={true} height={30} />
                        ) : (
                            <Stack spacing={8}>
                                <Body>
                                    Show customized nudges per page to help visitors start a
                                    conversation.
                                </Body>
                                <Stack isHorizontal={true} spacing={8}>
                                    <Switch
                                        onChange={() => {
                                            props.setIsNudgeEnabled(!props.isNudgeEnabled);
                                            props.handleSave(
                                                {nudgeSettings: {isEnabled: !props.isNudgeEnabled}},
                                                SAVE_STATE_KEYS.SMART_NUDGES
                                            );
                                        }}
                                        isDisabled={props.hasNoKnowledgeContent}
                                        isRounded={true}
                                        isOn={areNudgesShownAsDisabled}
                                        isSmall={true}
                                    />
                                    <Body> {areNudgesShownAsDisabled ? 'On' : 'Off'}</Body>
                                </Stack>
                                {props.hasNoKnowledgeContent && (
                                    <Body size='normal' color='light'>
                                        Add{' '}
                                        <Link
                                            // @ts-expect-error
                                            as={RouterLink}
                                            size='normal'
                                            variant='primary'
                                            to='/inbox/settings/chat/knowledge'
                                        >
                                            content from your website
                                        </Link>{' '}
                                        to enable smart nudges.
                                    </Body>
                                )}
                                {areNudgesShownAsDisabled && (
                                    <SegmentedControl
                                        buttonOptions={NUDGE_DEVICE_VISIBILITY_OPTIONS}
                                        selectedValue={props.nudgeDeviceVisibility}
                                        setSelectedValue={(newValue) => {
                                            props.setNudgeDeviceVisibility(newValue);
                                            props.handleSave(
                                                {nudgeSettings: {deviceVisibility: newValue}},
                                                SAVE_STATE_KEYS.SMART_NUDGES
                                            );
                                        }}
                                        isAnimated={true}
                                    />
                                )}
                            </Stack>
                        )
                    }
                />
                <LabelWithContent
                    label='Call to action'
                    state={
                        props.savingToastKey === SAVE_STATE_KEYS.CALL_TO_ACTION
                            ? props.toastState
                            : 'initial'
                    }
                    spacing={8}
                    content={
                        props.isLoading ? (
                            <LoadingText isFullWidth={true} height={30} />
                        ) : (
                            <CharacterLimitedTextfield
                                value={nudgeCallToAction}
                                onChange={setNudgeCallToAction}
                                onBlur={onNudgeCallToActionBlur}
                                maxLength={NUDGE_CALL_TO_ACTION_LIMIT}
                                placeholder='Ask anything!'
                                isDebouncedSave={true}
                                debouncedSaveDelay={SAVE_DELAY}
                            />
                        )
                    }
                />
            </Stack>
        </div>
    );
}
